import { productStatusSchema, productTypeSchema, SHELF_LIFE_CRITICAL_DAYS } from '@erp/shared';
import { AlertTriangle, CalendarClock, CheckCircle2, Filter, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  EMPTY_PRODUCT_FILTERS,
  hasActiveProductFilters,
  type ProductFiltersValue,
} from '@/features/products/lib/product-filters-state';

export interface ProductFiltersProps {
  value: ProductFiltersValue;
  onChange: (value: ProductFiltersValue) => void;
  availableGroups: { value: string; count: number }[];
  belowMinStockCount: number;
  expiringCount: number;
}

function toggle(list: string[], entry: string): string[] {
  return list.includes(entry) ? list.filter((item) => item !== entry) : [...list, entry];
}

export function ProductFilters({
  value,
  onChange,
  availableGroups,
  belowMinStockCount,
  expiringCount,
}: ProductFiltersProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        variant={value.belowMinStock ? 'default' : 'outline'}
        size="sm"
        aria-pressed={value.belowMinStock}
        onClick={() => onChange({ ...value, belowMinStock: !value.belowMinStock })}
      >
        <AlertTriangle className="h-4 w-4" aria-hidden="true" />
        {t('products.filters.criticalStock')}
        {belowMinStockCount > 0 ? <Badge variant="secondary">{belowMinStockCount}</Badge> : null}
      </Button>

      <Button
        variant={value.expiringWithinDays === undefined ? 'outline' : 'default'}
        size="sm"
        aria-pressed={value.expiringWithinDays !== undefined}
        onClick={() =>
          onChange({
            ...value,
            expiringWithinDays:
              value.expiringWithinDays === undefined ? SHELF_LIFE_CRITICAL_DAYS : undefined,
          })
        }
      >
        <CalendarClock className="h-4 w-4" aria-hidden="true" />
        {t('products.filters.expiring', { days: SHELF_LIFE_CRITICAL_DAYS })}
        {expiringCount > 0 ? <Badge variant="secondary">{expiringCount}</Badge> : null}
      </Button>

      <Button
        variant={value.activeOnly ? 'default' : 'outline'}
        size="sm"
        aria-pressed={value.activeOnly}
        onClick={() => onChange({ ...value, activeOnly: !value.activeOnly })}
      >
        <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
        {t('products.filters.activeOnly')}
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4" aria-hidden="true" />
            {t('products.filters.status')}
            {value.status.length > 0 ? (
              <Badge variant="secondary">{value.status.length}</Badge>
            ) : null}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuLabel>{t('products.filters.status')}</DropdownMenuLabel>
          {productStatusSchema.options.map((status) => (
            <DropdownMenuCheckboxItem
              key={status}
              checked={value.status.includes(status)}
              onCheckedChange={() => onChange({ ...value, status: toggle(value.status, status) })}
            >
              {t(`products.statuses.${status}`)}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4" aria-hidden="true" />
            {t('products.filters.type')}
            {value.type.length > 0 ? <Badge variant="secondary">{value.type.length}</Badge> : null}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuLabel>{t('products.filters.type')}</DropdownMenuLabel>
          {productTypeSchema.options.map((type) => (
            <DropdownMenuCheckboxItem
              key={type}
              checked={value.type.includes(type)}
              onCheckedChange={() => onChange({ ...value, type: toggle(value.type, type) })}
            >
              {t(`products.types.${type}`)}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" disabled={availableGroups.length === 0}>
            <Filter className="h-4 w-4" aria-hidden="true" />
            {t('products.filters.productGroup')}
            {value.productGroup.length > 0 ? (
              <Badge variant="secondary">{value.productGroup.length}</Badge>
            ) : null}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
          <DropdownMenuLabel>{t('products.filters.productGroup')}</DropdownMenuLabel>
          {availableGroups.map((group) => (
            <DropdownMenuCheckboxItem
              key={group.value}
              checked={value.productGroup.includes(group.value)}
              onCheckedChange={() =>
                onChange({ ...value, productGroup: toggle(value.productGroup, group.value) })
              }
            >
              {group.value} ({group.count})
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4" aria-hidden="true" />
            {t('products.filters.batchManaged')}
            {value.isBatchManaged === undefined ? null : <Badge variant="secondary">1</Badge>}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuLabel>{t('products.filters.batchManaged')}</DropdownMenuLabel>
          <DropdownMenuCheckboxItem
            checked={value.isBatchManaged === true}
            onCheckedChange={(checked) =>
              onChange({ ...value, isBatchManaged: checked === true ? true : undefined })
            }
          >
            {t('products.filters.batchManagedOnly')}
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={value.isBatchManaged === false}
            onCheckedChange={(checked) =>
              onChange({ ...value, isBatchManaged: checked === true ? false : undefined })
            }
          >
            {t('products.filters.batchManagedNone')}
          </DropdownMenuCheckboxItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {hasActiveProductFilters(value) ? (
        <Button variant="ghost" size="sm" onClick={() => onChange(EMPTY_PRODUCT_FILTERS)}>
          <X className="h-4 w-4" aria-hidden="true" />
          {t('products.filters.reset')}
        </Button>
      ) : null}
    </div>
  );
}
